import type { Question } from './topics';
import { options, pick } from './util';

// ─────────────────────────────────────────────────────────────────────────────
// French, by rule. Only verbs that really are regular in the tense drilled —
// no acheter/préférer/appeler/payer (stem changes), no prendre (irregular).
// ─────────────────────────────────────────────────────────────────────────────

type P = { labels: string[]; plural: boolean };
const PEOPLE: P[] = [
  { labels: ['je'], plural: false },
  { labels: ['tu'], plural: false },
  { labels: ['il', 'elle', 'on'], plural: false },
  { labels: ['nous'], plural: true },
  { labels: ['vous'], plural: true },
  { labels: ['ils', 'elles'], plural: true },
];

const ER = ['e', 'es', 'e', 'ons', 'ez', 'ent'];
const IR = ['is', 'is', 'it', 'issons', 'issez', 'issent'];
const RE = ['s', 's', '', 'ons', 'ez', 'ent'];

export const FR_ER: Record<string, string> = {
  parler: 'to speak', aimer: 'to like / love', chanter: 'to sing', danser: 'to dance', écouter: 'to listen',
  regarder: 'to watch', travailler: 'to work', jouer: 'to play', habiter: 'to live', manger: 'to eat',
  nager: 'to swim', commencer: 'to start', étudier: 'to study', donner: 'to give', trouver: 'to find',
  arriver: 'to arrive', entrer: 'to enter', marcher: 'to walk', oublier: 'to forget', aider: 'to help',
};
export const FR_IR: Record<string, string> = {
  finir: 'to finish', choisir: 'to choose', réussir: 'to succeed', grandir: 'to grow up', remplir: 'to fill',
  obéir: 'to obey', réfléchir: 'to think', rougir: 'to blush', maigrir: 'to lose weight', punir: 'to punish',
};
export const FR_RE: Record<string, string> = {
  vendre: 'to sell', attendre: 'to wait', entendre: 'to hear', répondre: 'to answer', perdre: 'to lose',
  descendre: 'to go down', rendre: 'to give back', défendre: 'to defend',
};

const startsWithVowel = (s: string) => /^[aeiouyhéèêàâîôû]/i.test(s);

/** Present tense of a regular -er / -ir / -re verb from the lists above. */
export function conj(verb: string, i: number): string {
  if (verb in FR_ER) {
    let stem = verb.slice(0, -2);
    if (i === 3 && stem.endsWith('g')) stem += 'e';                     // mangeons
    if (i === 3 && stem.endsWith('c')) stem = stem.slice(0, -1) + 'ç';  // commençons
    return stem + ER[i];
  }
  if (verb in FR_IR) return verb.slice(0, -2) + IR[i];
  return verb.slice(0, -2) + RE[i];
}

/** "je ___" or "j'___" — the pronoun elides before a vowel or mute h. */
function subject(i: number, form: string): string {
  const who = pick(PEOPLE[i].labels);
  return who === 'je' && startsWithVowel(form) ? "j'" : who;
}

function verbQuestions(topic: string, bank: Record<string, string>, n: number): Question[] {
  const verbs = Object.keys(bank);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const verb = pick(verbs);
    const i = Math.floor(Math.random() * 6);
    const answer = conj(verb, i);
    const who = subject(i, answer);
    const typed = Math.random() < 0.5;
    qs.push({
      key: `${topic}:${verb}:${i}`, topic: topic as Question['topic'], kind: typed ? 'type' : 'choice',
      instruction: typed ? 'Present tense · write the verb' : 'Present tense · choose the verb',
      prompt: who.endsWith("'") ? `${who}___ (${verb})` : `${who} ___ (${verb})`, hint: bank[verb], answers: [answer],
      options: options(answer, [0, 1, 2, 3, 4, 5].map(j => conj(verb, j))),
    });
  }
  return qs;
}

export const frEr = (n: number) => verbQuestions('fr_er', FR_ER, n);
export const frIr = (n: number) => verbQuestions('fr_ir', FR_IR, n);
export const frRe = (n: number) => verbQuestions('fr_re', FR_RE, n);

const ETRE = ['suis', 'es', 'est', 'sommes', 'êtes', 'sont'];
const AVOIR = ['ai', 'as', 'a', 'avons', 'avez', 'ont'];

export function frEtreAvoir(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const etre = Math.random() < 0.5;
    const table = etre ? ETRE : AVOIR;
    const i = Math.floor(Math.random() * 6);
    const answer = table[i];
    const who = etre ? pick(PEOPLE[i].labels) : subject(i, answer);
    const typed = Math.random() < 0.4;
    qs.push({
      key: `fr_etre_avoir:${etre}:${i}`, topic: 'fr_etre_avoir', kind: typed ? 'type' : 'choice',
      instruction: `${etre ? 'Être' : 'Avoir'} · present tense`,
      prompt: who.endsWith("'") ? `${who}___ (${etre ? 'être' : 'avoir'})` : `${who} ___ (${etre ? 'être' : 'avoir'})`,
      hint: etre ? 'to be' : 'to have', answers: [answer],
      options: options(answer, table),
    });
  }
  return qs;
}

/** Passé composé: which auxiliary? The DR & MRS VANDERTRAMP verbs take être. */
const PC: { inf: string; pp: string; etre: boolean; en: string }[] = [
  { inf: 'aller', pp: 'allé', etre: true, en: 'went' }, { inf: 'venir', pp: 'venu', etre: true, en: 'came' },
  { inf: 'arriver', pp: 'arrivé', etre: true, en: 'arrived' }, { inf: 'partir', pp: 'parti', etre: true, en: 'left' },
  { inf: 'entrer', pp: 'entré', etre: true, en: 'came in' }, { inf: 'sortir', pp: 'sorti', etre: true, en: 'went out' },
  { inf: 'naître', pp: 'né', etre: true, en: 'was born' }, { inf: 'mourir', pp: 'mort', etre: true, en: 'died' },
  { inf: 'rester', pp: 'resté', etre: true, en: 'stayed' }, { inf: 'tomber', pp: 'tombé', etre: true, en: 'fell' },
  { inf: 'monter', pp: 'monté', etre: true, en: 'went up' }, { inf: 'devenir', pp: 'devenu', etre: true, en: 'became' },
  { inf: 'revenir', pp: 'revenu', etre: true, en: 'came back' }, { inf: 'retourner', pp: 'retourné', etre: true, en: 'went back' },
  { inf: 'manger', pp: 'mangé', etre: false, en: 'ate' }, { inf: 'parler', pp: 'parlé', etre: false, en: 'spoke' },
  { inf: 'finir', pp: 'fini', etre: false, en: 'finished' }, { inf: 'vendre', pp: 'vendu', etre: false, en: 'sold' },
  { inf: 'voir', pp: 'vu', etre: false, en: 'saw' }, { inf: 'faire', pp: 'fait', etre: false, en: 'did / made' },
  { inf: 'prendre', pp: 'pris', etre: false, en: 'took' }, { inf: 'boire', pp: 'bu', etre: false, en: 'drank' },
  { inf: 'lire', pp: 'lu', etre: false, en: 'read' }, { inf: 'écrire', pp: 'écrit', etre: false, en: 'wrote' },
  { inf: 'regarder', pp: 'regardé', etre: false, en: 'watched' }, { inf: 'acheter', pp: 'acheté', etre: false, en: 'bought' },
];

export function frPasseCompose(n: number): Question[] {
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const v = pick(PC);
    const i = Math.floor(Math.random() * 6);
    const right = (v.etre ? ETRE : AVOIR)[i];
    const wrong = (v.etre ? AVOIR : ETRE)[i];
    const who = i === 0 && right === 'ai' ? "j'" : pick(PEOPLE[i].labels);
    const pp = v.etre ? `${v.pp}${PEOPLE[i].plural ? '(e)s' : '(e)'}` : v.pp;
    qs.push({
      key: `fr_pc:${v.inf}:${i}`, topic: 'fr_passe_compose', kind: 'choice',
      instruction: 'Passé composé · avoir or être?',
      prompt: who.endsWith("'") ? `${who}___ ${pp}` : `${who} ___ ${pp}`, hint: `${v.inf}: ${v.en}`,
      why: v.etre ? `${v.inf} is a DR & MRS VANDERTRAMP verb → être` : `${v.inf} → avoir (most verbs)`,
      options: options(right, [wrong, wrong === 'ai' ? 'suis' : wrong]), answers: [right],
    });
  }
  return qs;
}

const FR_NOUNS: { w: string; g: 'm' | 'f'; en: string; trap?: boolean }[] = [
  { w: 'livre', g: 'm', en: 'book' }, { w: 'garçon', g: 'm', en: 'boy' }, { w: 'fille', g: 'f', en: 'girl' },
  { w: 'maison', g: 'f', en: 'house' }, { w: 'table', g: 'f', en: 'table' }, { w: 'chat', g: 'm', en: 'cat' },
  { w: 'voiture', g: 'f', en: 'car' }, { w: 'stylo', g: 'm', en: 'pen' }, { w: 'école', g: 'f', en: 'school' },
  { w: 'arbre', g: 'm', en: 'tree' }, { w: 'fleur', g: 'f', en: 'flower' }, { w: 'jour', g: 'm', en: 'day' },
  { w: 'nuit', g: 'f', en: 'night' }, { w: 'semaine', g: 'f', en: 'week' }, { w: 'pays', g: 'm', en: 'country' },
  { w: 'ville', g: 'f', en: 'city' }, { w: 'pomme', g: 'f', en: 'apple' }, { w: 'cahier', g: 'm', en: 'notebook' },
  { w: 'chaise', g: 'f', en: 'chair' }, { w: 'ordinateur', g: 'm', en: 'computer' }, { w: 'chanson', g: 'f', en: 'song' },
  { w: 'poisson', g: 'm', en: 'fish' }, { w: 'téléphone', g: 'm', en: 'phone' }, { w: 'vélo', g: 'm', en: 'bike' },
  { w: 'nation', g: 'f', en: 'nation' }, { w: 'sport', g: 'm', en: 'sport' },
  // Traps: endings that point the wrong way.
  { w: 'problème', g: 'm', en: 'problem', trap: true }, { w: 'musée', g: 'm', en: 'museum', trap: true },
  { w: 'lycée', g: 'm', en: 'high school', trap: true }, { w: 'fromage', g: 'm', en: 'cheese', trap: true },
  { w: 'message', g: 'm', en: 'message', trap: true }, { w: 'voyage', g: 'm', en: 'trip', trap: true },
  { w: 'page', g: 'f', en: 'page', trap: true }, { w: 'plage', g: 'f', en: 'beach', trap: true },
  { w: 'image', g: 'f', en: 'picture', trap: true }, { w: 'main', g: 'f', en: 'hand', trap: true },
  { w: 'photo', g: 'f', en: 'photo', trap: true }, { w: 'couleur', g: 'f', en: 'colour', trap: true },
  { w: 'souris', g: 'f', en: 'mouse', trap: true }, { w: 'bonheur', g: 'm', en: 'happiness', trap: true },
];

export function frGender(n: number): Question[] {
  const traps = FR_NOUNS.filter(x => x.trap);
  const plain = FR_NOUNS.filter(x => !x.trap);
  const qs: Question[] = [];
  for (let k = 0; k < n; k++) {
    const x = Math.random() < 0.45 ? pick(traps) : pick(plain);
    qs.push({
      key: `fr_gender:${x.w}`, topic: 'fr_gender', kind: 'choice', instruction: 'Un or une?',
      prompt: `___ ${x.w}`, hint: x.en, options: ['un', 'une'], answers: [x.g === 'm' ? 'un' : 'une'],
      ...(x.trap ? { why: x.g === 'm' ? `${x.w} is masculine, despite how it looks` : `${x.w} is feminine, despite how it looks` } : {}),
    });
  }
  return qs;
}
